import React, { useCallback, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Edit from "@mui/icons-material/Edit";
import Check from "@mui/icons-material/Check";
import { useTheme } from "@mui/material/styles";
import ProgramDayScheduleItemForm from "../ProgramDayScheduleItemForm/ProgramDayScheduleItemForm";

export default function SortableScheduleItem({
  setState,
  id,
  title,
  expanded,
  edited,
  data,
  order,
  programDay,
}) {
  const scheduleItem = data;
  const { palette: muiPalette } = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleClick = () => {
    setState(id, {
      expanded: !expanded,
    });
  };

  const setEdited = useCallback(
    (edited) => {
      setState(id, {
        edited: edited,
      });
    },
    [id, setState]
  );

  const handleFormChanged = useCallback(() => {
    if (!edited) {
      setEdited(true);
    }
  }, [edited, setEdited]);

  const handleFormSaved = useCallback(
    (newScheduleItem) => {
      if (!edited) {
        return;
      }
      setState(id, {
        data: newScheduleItem,
        edited: false,
      });
    },
    [edited, id, setState]
  );

  const handleItemDeleted = useCallback(() => {
    setState(id, {
      removed: true,
    });
  }, [setState, id]);

  useEffect(() => {
    if (!(data.uuid || edited)) {
      setEdited(true);
    }
  }, [data, edited, setEdited]);

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      sx={{
        margin: "1rem",
      }}
    >
      <Accordion
        expanded={expanded}
        sx={{
          width: "100%",
          padding: 0,
          margin: 0,
          border: `1px solid ${muiPalette.divider}`,
        }}
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent="space-between"
            sx={{
              width: "100%",
            }}
          >
            <Typography>{scheduleItem?.label || title}</Typography>
            {edited ? (
              <Edit color="warning" />
            ) : (
              <Check
                color="success"
                sx={{
                  opacity: ".5",
                }}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          onClick={(event) => event.stopPropagation()}
          sx={{
            padding: "1rem",
          }}
        >
          <ProgramDayScheduleItemForm
            scheduleItem={scheduleItem}
            onChange={handleFormChanged}
            onSave={handleFormSaved}
            onDelete={handleItemDeleted}
            programDay={programDay}
            edited={edited}
            order={order}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
